import * as React from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { useToasts } from "react-toast-notifications";
import { DateFormats, IArchivedClient } from "intake_shared/src/types";
import moment from "../../../../utils/app_moment";
import store from "../../../../store";
import { AclContext } from "../../../../contexts/AclContext";
import { Button } from "../../bits/Button/Button";
import { Dialog } from "../../bits/Dialog/Dialog";
import { Select } from "../../bits/Select/Select";
import { COMPLETION_REASONS } from "../client-forms/CompleteClientFlow/CompleteClientFlow";
import EditIcon from "../../icons/Edit";
import styles from "./CompletionReasonEditor.module.scss";

interface CompletionReasonEditorProps {
    client: IArchivedClient;
    onSaved: (reason: string) => void;
}

export default function CompletionReasonEditor({
    client,
    onSaved,
}: CompletionReasonEditorProps) {
    const [open, setOpen] = React.useState(false);
    const [reason, setReason] = React.useState("");
    const [saving, setSaving] = React.useState(false);
    const loggedInUserEmail = React.useContext(AclContext)?.authUser.user?.email;
    const { addToast } = useToasts();
    const reasonOptions = Array.from(
        new Set([...COMPLETION_REASONS, client.complete_notes?.reason])
    )
        .filter((reason): reason is string => Boolean(reason))
        .map((reason) => ({ label: reason, value: reason }));

    const handleSave = async () => {
        if (!loggedInUserEmail || !reason || saving) {
            return;
        }

        setSaving(true);
        try {
            const changed = await store.updateArchivedClientCompletionReason(
                client.id,
                reason,
                loggedInUserEmail
            );
            if (changed) {
                onSaved(reason);
                addToast("Completion reason updated", {
                    appearance: "success",
                });
            }
            setOpen(false);
        } catch {
            addToast("Unable to update completion reason. Please try again.", {
                appearance: "error",
            });
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <OverlayTrigger
                placement="top"
                overlay={
                    <Tooltip id={`edit-completion-reason-${client.id}`}>
                        Edit
                    </Tooltip>
                }
            >
                <Button
                    variant="normal"
                    size="auto"
                    className={styles.editIconButton}
                    aria-label={`Edit completion reason for client ${client.id.slice(
                        0,
                        6
                    )}`}
                    onClick={() => {
                        setReason(client.complete_notes?.reason ?? "");
                        setOpen(true);
                    }}
                >
                    <EditIcon />
                </Button>
            </OverlayTrigger>
            <Dialog
                open={open}
                title="Edit completion reason"
                onClose={() => {
                    if (!saving) {
                        setOpen(false);
                    }
                }}
                onSave={handleSave}
                saveText="Save reason"
                isSubmitBtnDisabled={
                    saving ||
                    !loggedInUserEmail ||
                    !reason ||
                    reason === client.complete_notes?.reason
                }
                isCancelBtnDisabled={saving}
            >
                <div className={styles.fields}>
                    <div>
                        <strong>Closed on</strong>
                        <p>
                            {client.archived_on
                                ? moment(client.archived_on).format(
                                      DateFormats.DAY_MONTH_YEAR_FORMAT
                                  )
                                : "Not available"}
                        </p>
                    </div>
                    <div>
                        <strong>Completion notes</strong>
                        <p>
                            {client.complete_notes?.notes || "No completion notes"}
                        </p>
                    </div>
                    <Select
                        label="Completion reason"
                        options={reasonOptions}
                        value={reason ? { label: reason, value: reason } : null}
                        onChange={(option) =>
                            setReason(
                                (option as { value: string } | null)?.value ?? ""
                            )
                        }
                        isDisabled={saving}
                        isLongListInDialog
                    />
                </div>
            </Dialog>
        </>
    );
}
